/**
 * Fetch wrapper integrating the gateway session and offline queueing.
 *
 * MIGRATION (Rook, 2026-08-07): every call used to go to an ABSOLUTE
 * `VITE_API_URL ?? 'http://localhost:3000'` with a Bearer token. No env file ever set
 * VITE_API_URL, so the DEPLOYED bundle literally asked the operator's own machine for the
 * API — over http, from an https page, so the browser blocked it as mixed content before it
 * could even fail honestly. Nothing in the app could reach the backend, including login.
 *
 * Now: SAME-ORIGIN `/api/...`, which the gateway proxies to master-layer, translating the
 * httpOnly session cookie into a downstream Bearer. There is no build-time URL left to get
 * wrong, and no second auth model to keep in sync with hop's.
 */
import { API_PREFIX, requestHeaders, isPasswordChangeRequired, announcePasswordChangeRequired } from './auth'
import { enqueueRequest } from './offlineQueue'
import type {
  DispatchDriver,
  FinalCheckOrder,
  HttpMethod,
  LookupableOrder,
  QueuedRequestBody,
  ReadyToSendOrder,
  InFlightOrder,
} from '../types'

export { API_PREFIX }

// R363: a read the server answers 401 means the session is gone; the app registers the same
// handler the offline queue uses (logout, then the login screen).
let readAuthExpiredHandler: (() => void | Promise<void>) | null = null

export function setReadAuthExpiredHandler(fn: (() => void | Promise<void>) | null): void {
  readAuthExpiredHandler = fn
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_PREFIX}${path}`, { headers: requestHeaders('GET', false) })
  if (!res.ok) {
    if (res.status === 401 && readAuthExpiredHandler) {
      void readAuthExpiredHandler()
    }
    throw new Error(`GET ${path} failed: ${res.status}`)
  }
  return res.json() as Promise<T>
}

/**
 * bench-CASHIER-QUEUE-TRUTH.1 — the outcome of a state-changing request. `queued` means the
 * request never reached the server (genuine transport failure, offline is legitimate); `sent`
 * means the server accepted it; `rejected` means a response ARRIVED and the server refused it
 * (4xx/5xx). "queued" is deliberately unreachable from `rejected` — a received response is never
 * a queueing situation, no matter its status code.
 */
export type SendOutcome =
  | { outcome: 'sent' }
  | { outcome: 'queued' }
  | { outcome: 'rejected'; status: number; message: string }

/**
 * bench-CASHIER-QUEUE-TRUTH.1 §2 — master-layer's envelope NESTS the message under `error`;
 * it does not sit at the top level the way `error-codes.ts:27`'s published type claims. That
 * type is false at runtime (validation errors ship `message` as a `string[]`), so reading a
 * top-level `message` fell through to the generic fallback on every real rejection (Frank,
 * prod). Shapes, in the order checked (fixture: error-envelopes.json, `raw` is authoritative):
 *   1. nested string        — error.message is a string
 *   2. nested array         — error.message is a string[] — every element joined, not message[0]
 *   3. error-as-string      — error itself is a bare string (gateway-produced)
 *   4. flat                 — top-level message (fallback; not currently produced by any leg)
 */
async function extractErrorMessage(res: Response): Promise<string> {
  let data: unknown
  try {
    data = await res.json()
  } catch {
    return `Request rejected (${res.status})`
  }

  if (data && typeof data === 'object' && 'error' in data) {
    const error = (data as { error: unknown }).error

    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message: unknown }).message
      if (typeof message === 'string') {
        return message
      }
      if (Array.isArray(message) && message.every((m) => typeof m === 'string')) {
        return (message as string[]).join(' ')
      }
    }

    if (typeof error === 'string') {
      return error
    }
  }

  if (data && typeof data === 'object' && typeof (data as { message?: unknown }).message === 'string') {
    return (data as { message: string }).message
  }

  return `Request rejected (${res.status})`
}

/**
 * Send a state-changing request. If the network request itself fails (fetch rejects — offline
 * or unreachable), the request is queued for replay instead of being lost. A response that
 * ARRIVES, ok or not, is never queued — the caller gets the real outcome back.
 *
 * MIGRATION (bench-CASHIER-QUEUE-TRUTH.1): the prior version threw on `!res.ok` and let the
 * catch below swallow it into `enqueueRequest`, indistinguishable from a genuine network
 * failure. A permanent server rejection (404, 409) was rendered to the operator as "queued —
 * will sync when back online", telling him his money was captured at the moment the server told
 * him it was already captured. See the bench for the prod incident (Frank, z#12486/z#12492).
 */
export async function apiSend(
  method: HttpMethod,
  path: string,
  body: QueuedRequestBody,
): Promise<SendOutcome> {
  if (!navigator.onLine) {
    await enqueueRequest(method, path, body)
    return { outcome: 'queued' }
  }

  let res: Response
  try {
    res = await fetch(`${API_PREFIX}${path}`, {
      method,
      headers: requestHeaders(method, true),
      body: JSON.stringify(body),
    })
  } catch {
    await enqueueRequest(method, path, body)
    return { outcome: 'queued' }
  }

  if (res.ok) return { outcome: 'sent' }

  if (res.status === 403 && (await isPasswordChangeRequired(res))) {
    // R413: not a failure. The person must choose their own password first; the request is
    // kept pending (queued under this person) and the change screen is shown.
    await enqueueRequest(method, path, body)
    announcePasswordChangeRequired()
    return { outcome: 'queued' }
  }

  return { outcome: 'rejected', status: res.status, message: await extractErrorMessage(res) }
}

/** R413: the outcome of a person changing their own password. */
export type ChangePasswordOutcome =
  | { ok: true }
  | { ok: false; reason: 'wrong-current' | 'locked' | 'offline' | 'other' }

/**
 * R413: POST /api/auth/me/password with the CSRF header, like every cashier write. On success
 * the gateway refuses the current token, so the caller signs the person out.
 */
export async function changeOwnPassword(current: string, next: string): Promise<ChangePasswordOutcome> {
  let res: Response
  try {
    res = await fetch(`${API_PREFIX}/auth/me/password`, {
      method: 'POST',
      headers: requestHeaders('POST', true),
      body: JSON.stringify({ currentPassword: current, newPassword: next }),
    })
  } catch {
    return { ok: false, reason: 'offline' }
  }
  if (res.ok) return { ok: true }
  if (res.status === 403) return { ok: false, reason: 'wrong-current' }
  if (res.status === 429) return { ok: false, reason: 'locked' }
  return { ok: false, reason: 'other' }
}

/**
 * bench-CASHIER-TILL — record a till cash collection for an order (the pickup half of #92's
 * gate). Thin domain wrapper over apiSend; the transport/offline-queue behaviour is unchanged.
 */
export async function submitOrderCash(orderId: string, amountUsd: number): Promise<SendOutcome> {
  return apiSend('POST', `/orders/${orderId}/cash`, { amountUsd })
}

/**
 * bench-CASHIER-LOOKUP §3d.1 — the pickup till's tap-list: READY_FOR_DISPATCH pickup orders,
 * pos-reachable, scoped server-side (not a grant on the general orders collection).
 */
export async function getReadyForPickupOrders(): Promise<LookupableOrder[]> {
  return apiGet('/admin/orders/ready-for-pickup')
}

/**
 * bench-CASHIER-LOOKUP §3d.2 — order lookup by phone OR short code. ALWAYS returns every
 * match (§3g) — the caller renders a disambiguation list when there's more than one.
 */
export async function lookupOrders(query: string): Promise<LookupableOrder[]> {
  return apiGet(`/admin/customers/order-lookup?q=${encodeURIComponent(query)}`)
}

/**
 * R78 — Nour's final-check queue: every order, kitchen or shelf, stops here one step
 * before dispatch.
 */
export async function getAwaitingFinalCheckOrders(): Promise<FinalCheckOrder[]> {
  return apiGet('/admin/orders/awaiting-final-check')
}

/**
 * R78 — Nour's tap: the one writer of READY_FOR_DISPATCH. Same shape and queueing as
 * submitOrderCash. R197 (e3): sends the total the cashier was shown, so the server can refuse
 * a moved one. Only a two-decimal total is sent; an unreadable one is omitted (an unguarded
 * call), since the server would refuse a malformed value with a 400 forever. The queued item
 * carries the same body.
 */
const TWO_DECIMAL_TOTAL = /^-?\d+\.\d{2}$/

export async function submitFinalCheck(orderId: string, expectedTotalNow?: string): Promise<SendOutcome> {
  const body: QueuedRequestBody =
    typeof expectedTotalNow === 'string' && TWO_DECIMAL_TOTAL.test(expectedTotalNow) ? { expectedTotalNow } : {}
  return apiSend('POST', `/orders/${orderId}/final-check`, body)
}

/**
 * R462 - the orders in flight (confirmation until final check, every line with its kitchen
 * state), and the Packed tap that moves a mixed order to final check. Same shape and queueing
 * as submitFinalCheck; a replay onto an order already at final check gets the quiet 200.
 */
export async function getTillInFlightOrders(): Promise<InFlightOrder[]> {
  return apiGet('/admin/orders/till-in-flight')
}

export async function submitPackComplete(orderId: string): Promise<SendOutcome> {
  return apiSend('POST', `/orders/${orderId}/pack-complete`, {})
}

/**
 * R4 — Nour's "Send out" screen: the orders ready to go out, and the entity's drivers.
 * The run is created only on her tap (sendOut below) — there is no automatic builder.
 */
export async function getReadyToSend(): Promise<ReadyToSendOrder[]> {
  return apiGet('/dispatch/ready')
}

export async function getDrivers(): Promise<DispatchDriver[]> {
  return apiGet('/dispatch/drivers')
}

/**
 * R4 — Nour taps a free driver with at least one order ticked. Queues offline the way
 * submitFinalCheck does; the server's checks decide on replay.
 */
export async function sendOut(orderIds: string[], driverId: string): Promise<SendOutcome> {
  return apiSend('POST', '/dispatch/runs', { orderIds, driverId })
}
